import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/fbConfig';

const AssignTeachersToSubjectsModal = ({
  isOpen,
  onClose,
  classId,
  className,
  subjects,
  currentTerm,
  onSuccess
}) => {
  const [teachers, setTeachers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [assignments, setAssignments] = useState({});
  const [loading, setLoading] = useState(false);

  // Load teachers + current assignments
  useEffect(() => {
    if (!isOpen || !subjects.length) return;

    const load = async () => {
      setLoading(true);
      try {
        const snap = await getDocs(collection(db, 'users'));
        const allTeachers = snap.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(u => ['Teacher', 'Cashier', 'Admin'].includes(u.status) && u.isActive === true);

        // Build map: teacherId → list of subjectIds they teach in this class
        const currentAssignments = {};
        allTeachers.forEach(teacher => {
          const assignedSubjects = teacher.teachingAssignments
            ?.filter(a => a.classId === classId)
            .map(a => a.subjectId) || [];

          if (assignedSubjects.length > 0) {
            currentAssignments[teacher.id] = assignedSubjects;
          }
        });

        setAssignments(currentAssignments);
        setTeachers(allTeachers);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isOpen, classId, subjects]);

  // Toggle subject for a teacher
  const toggleSubjectForTeacher = (teacherId, subjectId) => {
    setAssignments(prev => {
      const current = prev[teacherId] || [];
      if (current.includes(subjectId)) {
        return { ...prev, [teacherId]: current.filter(id => id !== subjectId) };
      } else {
        return { ...prev, [teacherId]: [...current, subjectId] };
      }
    });
  };

  // Save all changes
  const saveAll = async () => {
    setLoading(true);
    try {
      const updates = [];

      for (const teacher of teachers) {
        const teacherRef = doc(db, 'users', teacher.id);
        const oldAssignments = teacher.teachingAssignments || [];

        // Remove all old assignments for this class
        let newAssignments = oldAssignments.filter(a => a.classId !== classId);

        // Add selected subjects
        const selectedSubjects = assignments[teacher.id] || [];
        selectedSubjects.forEach(subId => {
          newAssignments.push({ classId, subjectId: subId, termId: currentTerm?.termId, sessionId: currentTerm?.sessionId });
        });

        // Only write if changed
        if (JSON.stringify(newAssignments) !== JSON.stringify(oldAssignments)) {
          updates.push(updateDoc(teacherRef, { teachingAssignments: newAssignments }));
        }
      }

      await Promise.all(updates);
      onSuccess();
      onClose();
    } catch (err) {
      console.error("Save failed:", err);
    } finally {
      setLoading(false);
    }
  };

  // Filter teachers by name
  const filteredTeachers = teachers.filter(teacher =>
    `${teacher.firstName} ${teacher.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="custom-modal wide-modal"
      // ← make sure this class exists
      overlayClassName="custom-modal-overlay"
    >
      <h3 className="size5 caps" style={{ marginBottom: '16px' }}>
        Assign Subjects to Teachers — {className}
      </h3>

      {/* Search Bar */}
      <input
        type="text"
        placeholder="Search teachers..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="input-field1"
        style={{ width: '100%', marginBottom: '20px' }}
      />

      <div style={{ maxHeight: '65vh', overflowY: 'auto' }}>
        {loading ? (
          <p>Loading teachers...</p>
        ) : filteredTeachers.length === 0 ? (
          <p>No teachers found.</p>
        ) : (
          filteredTeachers.map(teacher => {
            const assignedSubjectIds = assignments[teacher.id] || [];

            return (
              <div
                key={teacher.id}
                className="info-item"
                style={{
                  marginBottom: '16px',
                  padding: '14px',
                  borderRadius: '8px',
                  backgroundColor: '#f9f9f9',
                  borderColor: '#ccc',
                  maxHeight: 'calc(100vh - 480px)',
                  overflowY: 'auto',
                  width: '100%'
                }}
              >
                <p className="bold size5">
                  {teacher.firstName} {teacher.lastName}
                  {assignedSubjectIds.length > 0 && (
                    <span style={{ color: 'green', flexDirection: 'column', fontSize: '0.85rem', marginLeft: '10px' }}>
                      ({assignedSubjectIds.length} subject{assignedSubjectIds.length > 1 ? 's' : ''})</span>
                  )}
                </p>

                <div className="column gap-1" style={{ marginTop: '10px' }}>
                  {subjects.map(subject => {
                    const isAssigned = assignedSubjectIds.includes(subject.id);
                    return (
                      <label
                        key={subject.id}
                        style={{
                          display: 'flex',                          
                          alignItems: 'center',
                          fontSize: '0.95rem',
                          backgroundColor: isAssigned ? '#e6f7e6' : '#f0f0f0',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isAssigned}
                          onChange={() => toggleSubjectForTeacher(teacher.id, subject.id)}
                          className="input-checkbox gold-tick"
                          style={{ marginRight: '8px' }}
                        />
                        {subject.name}
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="row align-center gap-15" style={{ marginTop: '20px' }}>
        <button onClick={onClose} className="login-button" disabled={loading}>
          Cancel
        </button>
        <button onClick={saveAll} className="login-button" disabled={loading}>
          {loading ? 'Saving...' : 'Save All'}
        </button>
      </div>
    </Modal>
  );
};

export default AssignTeachersToSubjectsModal;